import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useTranslation } from 'react-i18next';

function MarketOverview({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;

  const dist = summary.distribution || {};
  const chartData = [
    { name: t('saturation.levels.extreme', 'Cực cao'), value: dist.extreme || 0, color: '#d32f2f' },
    { name: t('saturation.levels.high', 'Cao'), value: dist.high || 0, color: '#f57c00' },
    { name: t('saturation.levels.moderate', 'Trung bình'), value: dist.moderate || 0, color: '#fbc02d' },
    { name: t('saturation.levels.low', 'Thấp'), value: dist.low || 0, color: '#388e3c' },
  ].filter(d => d.value > 0);

  const guides = [
    { range: '70–100', label: t('saturation.levels.extreme', 'Cực cao'), color: '#d32f2f', action: t('saturation.actions.avoid', 'Tránh / Giảm') },
    { range: '50–70', label: t('saturation.levels.high', 'Cao'), color: '#f57c00', action: t('saturation.actions.caution', 'Thận trọng') },
    { range: '30–50', label: t('saturation.levels.moderate', 'Trung bình'), color: '#fbc02d', action: t('saturation.actions.selective', 'Chọn lọc') },
    { range: '0–30', label: t('saturation.levels.low', 'Thấp'), color: '#388e3c', action: t('saturation.actions.attractive', 'Hấp dẫn') },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2 }, height: '100%' }}>
      {/* Distribution donut */}
      <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1, fontSize: '1rem' }}>
            {t('saturation.overview.marketDistribution', 'Phân bổ thị trường')}
          </Typography>

          <Box sx={{ height: 210, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={86}
                  paddingAngle={3}
                  dataKey="value"
                  strokeWidth={0}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center label */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: 'text.primary', lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                {summary.avg_saturation?.toFixed(0)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t('saturation.cards.averageSaturation', 'Trung bình')}
              </Typography>
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {chartData.map((d, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                  {d.name} ({d.value})
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Risk action guide */}
      <Card elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', flex: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1.5, fontSize: '1rem' }}>
            {t('saturation.overview.riskAssessment', 'Hướng dẫn hành động')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {guides.map((g, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: g.color + '06',
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: g.color, flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ fontSize: '0.78rem' }}>
                  {g.label}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontFeatureSettings: '"tnum"', fontSize: '0.68rem' }}>
                  {g.range}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color={g.color} fontWeight={600} sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                  {g.action}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default MarketOverview;
